import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTranslation } from 'react-i18next';

/**
 * ProtectedRoute component that redirects to login if user is not authenticated
 * @param {Object} props - Component props
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated (for backward compatibility)
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @returns {React.ReactNode} - The protected component or redirect
 */
const ProtectedRoute = ({ isAuthenticated, requiredRoles, children }) => {
  const location = useLocation();
  const { isAuthenticated: authContextAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  
  // Use either the prop or the context value (prefer context if available)
  const isUserAuthenticated = authContextAuthenticated !== undefined ? authContextAuthenticated : isAuthenticated;
  
  // If not authenticated, redirect to login
  if (!isUserAuthenticated) {
    // Redirect to login with the current location for redirect after login
    return (
      <Navigate 
        to="/login" 
        state={{ 
          from: location.pathname,
          message: t('protected.mustLogin')
        }}
        replace 
      />
    );
  }

  // If roles are specified, enforce role-based access
  if (requiredRoles && Array.isArray(requiredRoles) && requiredRoles.length) {
    const role = user?.role;
    const roles = user?.roles || (role ? [role] : []);

    // Super-admin bypass: Admin always has access to admin site pages
    if (roles.includes('admin')) {
      return children;
    }

    if (!roles.some(r => requiredRoles.includes(r))) {
      // Redirect to home (or a 403 page) with a message
      return (
        <Navigate
          to="/access-denied"
          state={{ message: t('protected.noPermission') }}
          replace
        />
      );
    }
  }

  return children;
};

export default ProtectedRoute;